import Skeleton from "./ui/Skeleton";
import "./DashboardSkeleton.css";

/**
 * Dashboard Skeleton.
 * Accurately matches the 3-column top row (Readiness Score, Priority Actions, Authority Scanner)
 * and 2-column middle row (Visibility Trend Chart, Score Distribution) of the SeenByAI Dashboard.
 */
export function DashboardSkeleton({ showSidebar = false, showHeader = false }) {
  const content = (
    <div className="dashboard-content-skeleton-wrapper" aria-label="Loading dashboard...">
      {/* ── Top Row Grid (3 Columns) ── */}
      <section className="dashboard-section top-row-grid">
        {/* Card 1: AI Readiness Score Card Skeleton */}
        <div className="dashboard-card score-card">
          <div className="card-header-row">
            <div className="card-title-wrap" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Skeleton style={{ width: 145, height: 18 }} />
              <Skeleton style={{ width: 16, height: 16, borderRadius: "50%" }} />
            </div>
          </div>

          <div className="score-content-body-flex">
            <div className="score-top-row">
              <div className="score-left-info" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {/* Score Value: 77 /100 */}
                <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                  <Skeleton style={{ width: 68, height: 42, borderRadius: "8px" }} />
                  <Skeleton style={{ width: 38, height: 18 }} />
                </div>
                {/* Score Badge: Good */}
                <Skeleton style={{ width: 56, height: 24, borderRadius: "12px" }} />
                {/* Delta: +11 pts vs Oct 6 */}
                <Skeleton style={{ width: 110, height: 14 }} />
              </div>

              {/* Circular Gauge Skeleton */}
              <div className="score-right-gauge">
                <div style={{ width: 110, height: 110, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Skeleton style={{ width: 110, height: 110, borderRadius: "50%" }} />
                  <div
                    style={{
                      position: "absolute",
                      width: 76,
                      height: 76,
                      borderRadius: "50%",
                      backgroundColor: "var(--dashboard-plan-card, #0d1527)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >
                    <Skeleton style={{ width: 20, height: 20, borderRadius: "4px" }} />
                    <Skeleton style={{ width: 44, height: 8 }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="score-divider" style={{ margin: "22px 0 16px 0", height: 1, backgroundColor: "rgba(255,255,255,0.06)" }} />

            {/* What this means */}
            <div className="score-bottom-section">
              <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "16px" }}>
                <Skeleton style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                  <Skeleton style={{ width: 110, height: 14 }} />
                  <Skeleton style={{ width: "95%", height: 12 }} />
                  <Skeleton style={{ width: "80%", height: 12 }} />
                </div>
              </div>
              <Skeleton style={{ width: 95, height: 14 }} />
            </div>
          </div>
        </div>

        {/* Card 2: Top Priority Actions Card Skeleton */}
        <div className="dashboard-card priority-actions-card">
          <div className="card-header-row mb-3">
            <Skeleton style={{ width: 160, height: 18 }} />
          </div>

          <div className="actions-list" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="action-item-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  background: "rgba(255, 255, 255, 0.02)",
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                  borderRadius: "10px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                  <Skeleton style={{ width: 34, height: 34, borderRadius: "8px", flexShrink: 0 }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
                    <Skeleton style={{ width: i === 1 ? "88%" : i === 2 ? "72%" : "82%", height: 13 }} />
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Skeleton style={{ width: 75, height: 18, borderRadius: "10px" }} />
                      <Skeleton style={{ width: 85, height: 18, borderRadius: "10px" }} />
                    </div>
                  </div>
                </div>
                <Skeleton style={{ width: 12, height: 12, borderRadius: "3px" }} />
              </div>
            ))}
          </div>

          <div className="card-footer-link-row" style={{ marginTop: "auto", paddingTop: "14px" }}>
            <Skeleton style={{ width: 175, height: 14 }} />
          </div>
        </div>

        {/* Card 3: AI Authority Scanner (PHASE 2) Card Skeleton */}
        <div className="dashboard-card authority-scanner-card">
          <div className="authority-card-header-row mb-3" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Skeleton style={{ width: 18, height: 18, borderRadius: "4px" }} />
              <Skeleton style={{ width: 150, height: 18 }} />
            </div>
            <Skeleton style={{ width: 68, height: 20, borderRadius: "10px" }} />
          </div>

          <div className="authority-scanner-content" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Skeleton style={{ width: "95%", height: 13 }} />
            <Skeleton style={{ width: "70%", height: 13, marginBottom: "4px" }} />

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {[1, 2, 3, 4, 5].map((idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Skeleton style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0 }} />
                  <Skeleton style={{ width: idx === 5 ? "85%" : `${68 + (idx % 3) * 8}%`, height: 12 }} />
                </div>
              ))}
            </div>

            <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <Skeleton style={{ width: 140, height: 34, borderRadius: "20px" }} />
              <Skeleton style={{ width: 230, height: 11 }} />
            </div>
          </div>
        </div>
      </section>

      {/* ── Middle Row Grid (AI Visibility Trend + Score Distribution) ── */}
      <section className="dashboard-section middle-row-section">
        {/* Card 1: AI Visibility Trend (Spans 2 columns) */}
        <div className="dashboard-card trend-card">
          <div className="card-header-row align-items-center mb-4" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Skeleton style={{ width: 165, height: 20 }} />

            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Skeleton style={{ width: 8, height: 8, borderRadius: "50%" }} />
                <Skeleton style={{ width: 80, height: 12 }} />
              </div>
              <div style={{ display: "flex", gap: "6px", background: "rgba(255,255,255,0.03)", padding: "4px 6px", borderRadius: "8px" }}>
                {[55, 58, 58, 62].map((w, idx) => (
                  <Skeleton key={idx} style={{ width: w, height: 24, borderRadius: "6px" }} />
                ))}
              </div>
            </div>
          </div>

          {/* Trend Chart Mock Area */}
          <div style={{ height: 220, display: "flex", alignItems: "flex-end", position: "relative", padding: "10px 0" }}>
            {/* Y axis numbers */}
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%", paddingRight: "16px" }}>
              {[100, 75, 50, 25, 0].map((n) => (
                <Skeleton key={n} style={{ width: 22, height: 10 }} />
              ))}
            </div>

            {/* Grid Lines + Simulated Area Line */}
            <div style={{ flex: 1, height: "100%", position: "relative", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              {[1, 2, 3, 4, 5].map((g) => (
                <div key={g} style={{ width: "100%", height: 1, background: "rgba(255,255,255,0.05)" }} />
              ))}
              <Skeleton
                style={{
                  position: "absolute",
                  bottom: 25,
                  left: 30,
                  right: 40,
                  height: 80,
                  borderRadius: "8px",
                  opacity: 0.35,
                }}
              />
            </div>
          </div>
        </div>

        {/* Card 2: Score Distribution (Spans 1 column) */}
        <div className="dashboard-card score-distribution-card">
          <div className="card-header-row mb-4">
            <Skeleton style={{ width: 140, height: 20 }} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, padding: "15px 0" }}>
            <div style={{ width: 140, height: 140, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Skeleton style={{ width: 140, height: 140, borderRadius: "50%" }} />
              <div
                style={{
                  position: "absolute",
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  backgroundColor: "var(--dashboard-plan-card, #0d1527)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                }}
              >
                <Skeleton style={{ width: 22, height: 22, borderRadius: "4px" }} />
                <Skeleton style={{ width: 56, height: 10 }} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  if (!showSidebar) {
    return (
      <div className="dashboard-skeleton-main" style={{ width: "100%", flex: 1 }}>
        {showHeader && (
          <header className="dashboard-skeleton-header">
            <div className="dashboard-skeleton-header-left">
              <Skeleton style={{ width: 220, height: 24, borderRadius: "6px" }} />
              <Skeleton style={{ width: 320, height: 14 }} />
            </div>
            <div className="dashboard-skeleton-header-right">
              <Skeleton style={{ width: 180, height: 38, borderRadius: "8px" }} />
              <Skeleton style={{ width: 130, height: 38, borderRadius: "8px" }} />
            </div>
          </header>
        )}
        {content}
      </div>
    );
  }

  return (
    <div className="dashboard-skeleton-layout" aria-label="Loading dashboard...">
      {/* ── Left Sidebar Skeleton ── */}
      <aside className="dashboard-skeleton-sidebar">
        <div className="dashboard-skeleton-sidebar-brand">
          <Skeleton style={{ width: 34, height: 34, borderRadius: "8px" }} />
          <Skeleton style={{ width: 120, height: 22, borderRadius: "6px" }} />
        </div>
        <nav className="dashboard-skeleton-nav">
          {[
            { width: 100, active: true },
            { width: 85 },
            { width: 110 },
            { width: 125 },
            { width: 105 },
            { width: 95 },
            { width: 115 },
            { width: 90 },
          ].map((item, index) => (
            <div
              key={index}
              className="dashboard-skeleton-nav-item"
              style={{
                background: item.active
                  ? "rgba(0, 229, 255, 0.08)"
                  : undefined,
                border: item.active
                  ? "1px solid rgba(0, 229, 255, 0.2)"
                  : undefined,
              }}
            >
              <Skeleton style={{ width: 18, height: 18, borderRadius: "4px" }} />
              <Skeleton style={{ width: item.width, height: 14 }} />
            </div>
          ))}
        </nav>
        <div className="dashboard-skeleton-sidebar-footer">
          <Skeleton style={{ width: 38, height: 38, borderRadius: "50%" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
            <Skeleton style={{ width: "80%", height: 12 }} />
            <Skeleton style={{ width: "50%", height: 10 }} />
          </div>
        </div>
      </aside>

      {/* ── Main Area ── */}
      <main className="dashboard-skeleton-main">
        {showHeader && (
          <header className="dashboard-skeleton-header">
            <div className="dashboard-skeleton-header-left">
              <Skeleton style={{ width: 220, height: 24, borderRadius: "6px" }} />
              <Skeleton style={{ width: 320, height: 14 }} />
            </div>
            <div className="dashboard-skeleton-header-right">
              <Skeleton style={{ width: 180, height: 38, borderRadius: "8px" }} />
              <Skeleton style={{ width: 130, height: 38, borderRadius: "8px" }} />
            </div>
          </header>
        )}
        {content}
      </main>
    </div>
  );
}

export default DashboardSkeleton;
